import React, { useState, useCallback, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import {
  Upload,
  FileSpreadsheet,
  Download,
  Loader2,
  Settings2,
  AlertCircle,
  CheckCircle2,
  X,
} from "lucide-react";

/**
 * GeneradorQRPorCentro
 * --------------------
 * Sube un Excel (.xlsx/.xls) o CSV, mapea las columnas, y genera un PDF por
 * cada centro educativo con una grilla de códigos QR (uno por persona).
 *
 * El contenido de cada QR se arma con una plantilla configurable, ej:
 *   {doc}|{fijo1}|{fijo2}|{nombre} {apellido}
 *
 * Dependencias (todas disponibles en el entorno de artifacts):
 *   xlsx, papaparse, qrcode, jspdf, jszip, lucide-react
 */

// --- Constantes de layout (mm sobre A4) ---
const PAGE = { w: 210, h: 297 };
const MARGIN = 12;
const HEADER_H = 22;
const GAP = 6;
const COLS = 3;
const ROWS = 4;

const TEAL = "#479EAC";
const DARK = "#1E3A40";

const DEFAULT_TEMPLATE = "{doc}|{fijo1}|{fijo2}|{nombre} {apellido}";

// Intenta adivinar qué columna corresponde a cada campo
function guessColumn(headers, candidates) {
  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  for (const cand of candidates) {
    const hit = headers.find((h) => norm(h).includes(cand));
    if (hit) return hit;
  }
  return "";
}

// Palabras que delatan una fila de encabezado real
const HEADER_HINTS = [
  "nombre",
  "apellido",
  "documento",
  "doc",
  "cedula",
  "ci",
  "dni",
  "centro",
  "correo",
  "email",
  "first",
  "last",
  "name",
];

const normTxt = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Recibe una matriz (array de filas) y devuelve array de objetos usando
// la fila que parezca el encabezado. Descarta títulos/metadatos de arriba.
function rowsFromMatrix(matrix) {
  let headerIdx = -1;
  let bestScore = 0;
  // Buscamos entre las primeras 25 filas la que más "pinta" de encabezado tenga
  const limit = Math.min(matrix.length, 25);
  for (let i = 0; i < limit; i++) {
    const cells = matrix[i].map(normTxt);
    let score = 0;
    for (const c of cells) {
      if (!c) continue;
      if (HEADER_HINTS.some((h) => c === h || c.includes(h))) score++;
    }
    // Preferimos la primera fila con al menos 2 coincidencias
    if (score >= 2 && score > bestScore) {
      bestScore = score;
      headerIdx = i;
    }
  }
  // Si no encontramos nada convincente, usamos la primera fila no vacía
  if (headerIdx === -1) {
    headerIdx = matrix.findIndex((r) =>
      r.some((c) => String(c ?? "").trim() !== "")
    );
    if (headerIdx === -1) return [];
  }

  const rawHeader = matrix[headerIdx];
  // Nombres de columna; rellena vacíos con Columna_N para no perder datos
  const header = rawHeader.map((h, i) => {
    const t = String(h ?? "").trim();
    return t || `Columna_${i + 1}`;
  });

  const objs = [];
  for (let r = headerIdx + 1; r < matrix.length; r++) {
    const row = matrix[r];
    if (!row.some((c) => String(c ?? "").trim() !== "")) continue;
    const obj = {};
    header.forEach((key, c) => {
      obj[key] = row[c] ?? "";
    });
    objs.push(obj);
  }
  return objs;
}

function safeName(name) {
  return (
    String(name || "")
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "_")
      .slice(0, 80) || "centro"
  );
}

function cleanDoc(v) {
  return String(v ?? "").replace(/[\s.\-]/g, "");
}

export default function GeneradorQRPorCentro() {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {done, total, label}
  const [showConfig, setShowConfig] = useState(false);
  const [zipUrl, setZipUrl] = useState("");
  const fileInputRef = useRef(null);

  const [map, setMap] = useState({
    nombre: "",
    apellido: "",
    doc: "",
    centro: "",
  });

  const [tmpl, setTmpl] = useState({
    template: DEFAULT_TEMPLATE,
    fijo1: "",
    fijo2: "",
    title: "Jornada de Intercambio · Empresas Juveniles 2026",
    noCentroLabel: "Sin centro asignado",
  });

  const reset = () => {
    setRows([]);
    setHeaders([]);
    setFileName("");
    setError("");
    setProgress(null);
    setZipUrl("");
    setMap({ nombre: "", apellido: "", doc: "", centro: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // --- Cargar archivo ---
  const handleFile = useCallback((file) => {
    if (!file) return;
    setError("");
    setZipUrl("");
    setFileName(file.name);

    const finish = (rawRows) => {
      // Filtra filas totalmente vacías
      const cleaned = rawRows.filter((r) =>
        Object.values(r).some((v) => String(v ?? "").trim() !== "")
      );
      if (cleaned.length === 0) {
        setError("El archivo no tiene filas con datos.");
        return;
      }
      const hdrs = Object.keys(cleaned[0]);
      setHeaders(hdrs);
      setRows(cleaned);
      setMap({
        nombre: guessColumn(hdrs, ["nombre", "first", "name"]),
        apellido: guessColumn(hdrs, ["apellido", "last", "surname"]),
        doc: guessColumn(hdrs, ["documento", "doc", "cedula", "ci", "dni"]),
        centro: guessColumn(hdrs, ["centro", "colegio", "liceo", "escuela", "institu"]),
      });
      setShowConfig(true);
    };

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "csv") {
      Papa.parse(file, {
        header: false, // leemos como matriz y detectamos el encabezado
        skipEmptyLines: true,
        complete: (res) => {
          const objs = rowsFromMatrix(res.data);
          if (!objs.length) {
            setError(
              "No se pudo detectar el encabezado del CSV. Revisá que tenga una fila con títulos como 'Nombre' o 'Documento'."
            );
            return;
          }
          finish(objs);
        },
        error: (err) => setError("No se pudo leer el CSV: " + err.message),
      });
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          // Leemos como matriz cruda (sin asumir que la fila 1 es el encabezado)
          const matrix = XLSX.utils.sheet_to_json(ws, {
            header: 1,
            defval: "",
            blankrows: false,
          });
          if (!matrix.length) {
            setError("El archivo está vacío.");
            return;
          }
          const objs = rowsFromMatrix(matrix);
          if (!objs.length) {
            setError(
              "No se pudo detectar el encabezado. Revisá que el archivo tenga una fila con títulos como 'Nombre' o 'Documento'."
            );
            return;
          }
          finish(objs);
        } catch (err) {
          setError("No se pudo leer el Excel: " + err.message);
        }
      };
      reader.onerror = () => setError("Error al leer el archivo.");
      reader.readAsArrayBuffer(file);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  // --- Preparar registros válidos y agruparlos por centro ---
  const groups = useMemo(() => {
    if (!rows.length || !map.doc) return null;
    const valid = [];
    let skipped = 0;
    for (const r of rows) {
      const nombre = String(r[map.nombre] ?? "").trim();
      const apellido = String(r[map.apellido] ?? "").trim();
      const doc = cleanDoc(r[map.doc]);
      // Necesitamos al menos un documento numérico y un nombre
      if (!doc || !/^\d+$/.test(doc) || (!nombre && !apellido)) {
        skipped++;
        continue;
      }
      let centro = String(r[map.centro] ?? "").trim();
      if (!centro) centro = tmpl.noCentroLabel;
      valid.push({ nombre, apellido, doc, centro });
    }
    const byCentro = {};
    for (const v of valid) (byCentro[v.centro] ||= []).push(v);
    for (const k of Object.keys(byCentro)) {
      byCentro[k].sort((a, b) =>
        (a.apellido + a.nombre).localeCompare(b.apellido + b.nombre, "es")
      );
    }
    return { byCentro, total: valid.length, skipped };
  }, [rows, map, tmpl.noCentroLabel]);

  function buildQrData(person) {
    return tmpl.template
      .replaceAll("{doc}", person.doc)
      .replaceAll("{fijo1}", tmpl.fijo1)
      .replaceAll("{fijo2}", tmpl.fijo2)
      .replaceAll("{nombre}", person.nombre)
      .replaceAll("{apellido}", person.apellido);
  }

  async function qrDataUrl(person) {
    return QRCode.toDataURL(buildQrData(person), {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 300,
    });
  }

  // --- Dibuja un PDF (jsPDF) para una lista de personas de un centro ---
  async function buildCentroPdf(centro, list) {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const cellW = (PAGE.w - 2 * MARGIN - (COLS - 1) * GAP) / COLS;
    const cellH =
      (PAGE.h - MARGIN - HEADER_H - MARGIN - (ROWS - 1) * GAP) / ROWS;
    const perPage = COLS * ROWS;
    const totalPages = Math.ceil(list.length / perPage);

    const drawHeader = (page) => {
      doc.setFillColor(TEAL);
      doc.rect(0, 0, PAGE.w, HEADER_H, "F");
      doc.setTextColor("#FFFFFF");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(tmpl.title, MARGIN, 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const c = centro.length > 78 ? centro.slice(0, 75) + "…" : centro;
      doc.text(c, MARGIN, 16);
      doc.text(`Pág. ${page}/${totalPages}`, PAGE.w - MARGIN, 16, {
        align: "right",
      });
    };

    for (let i = 0; i < list.length; i++) {
      const posOnPage = i % perPage;
      if (posOnPage === 0) {
        if (i > 0) doc.addPage();
        drawHeader(Math.floor(i / perPage) + 1);
      }
      const col = posOnPage % COLS;
      const row = Math.floor(posOnPage / COLS);
      const x = MARGIN + col * (cellW + GAP);
      const y = HEADER_H + MARGIN + row * (cellH + GAP);

      // marco de tarjeta
      doc.setDrawColor("#E6EEF0");
      doc.setLineWidth(0.3);
      doc.roundedRect(x, y, cellW, cellH, 2.5, 2.5, "S");

      // QR
      const person = list[i];
      const img = await qrDataUrl(person);
      const qrSide = Math.min(cellW - 16, cellH - 20);
      const qx = x + (cellW - qrSide) / 2;
      const qy = y + 5;
      doc.addImage(img, "PNG", qx, qy, qrSide, qrSide);

      // nombre
      const full = `${person.nombre} ${person.apellido}`.trim();
      doc.setTextColor(DARK);
      doc.setFont("helvetica", "bold");
      let fs = 11;
      doc.setFontSize(fs);
      while (doc.getTextWidth(full) > cellW - 8 && fs > 6) {
        fs -= 0.5;
        doc.setFontSize(fs);
      }
      doc.text(full, x + cellW / 2, y + cellH - 8, { align: "center" });

      // documento
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor("#666666");
      doc.text(`Doc: ${person.doc}`, x + cellW / 2, y + cellH - 3.5, {
        align: "center",
      });
    }
    return doc;
  }

  // --- Genera todos los PDFs y arma un ZIP ---
  async function generateAll() {
    if (!groups) return;
    setBusy(true);
    setZipUrl("");
    setError("");
    try {
      const entries = Object.entries(groups.byCentro);
      const zip = new JSZip();
      let done = 0;
      for (const [centro, list] of entries) {
        setProgress({ done, total: entries.length, label: centro });
        const pdf = await buildCentroPdf(centro, list);
        zip.file(`QR_${safeName(centro)}.pdf`, pdf.output("arraybuffer"));
        done++;
        setProgress({ done, total: entries.length, label: centro });
        // ceder al hilo para actualizar UI
        await new Promise((r) => setTimeout(r, 0));
      }
      const blob = await zip.generateAsync({ type: "blob" });
      setZipUrl(URL.createObjectURL(blob));
      setProgress(null);
    } catch (err) {
      setError("Falló la generación: " + err.message);
    } finally {
      setBusy(false);
    }
  }

  async function downloadOne(centro, list) {
    setBusy(true);
    try {
      const pdf = await buildCentroPdf(centro, list);
      pdf.save(`QR_${safeName(centro)}.pdf`);
    } catch (err) {
      setError("Falló el PDF de " + centro + ": " + err.message);
    } finally {
      setBusy(false);
    }
  }

  const mapReady = map.doc && (map.nombre || map.apellido);
  const centrosList = groups ? Object.entries(groups.byCentro) : [];

  return (
    <div
      style={{
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        color: DARK,
        maxWidth: 920,
        margin: "0 auto",
        padding: 24,
      }}
    >
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>
          Generador de QR por centro
        </h1>
        <p style={{ color: "#5a6b70", marginTop: 6, fontSize: 14 }}>
          Subí un Excel o CSV, mapeá las columnas y descargá un PDF de credenciales
          QR por cada centro educativo.
        </p>
      </header>

      {/* Zona de carga */}
      {!rows.length && (
        <div
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${TEAL}`,
            borderRadius: 16,
            padding: "48px 24px",
            textAlign: "center",
            cursor: "pointer",
            background: "#F7FBFC",
          }}
        >
          <Upload size={40} color={TEAL} style={{ marginBottom: 12 }} />
          <div style={{ fontWeight: 600 }}>
            Arrastrá tu archivo o hacé clic para elegirlo
          </div>
          <div style={{ color: "#7a898d", fontSize: 13, marginTop: 4 }}>
            Formatos: .xlsx, .xls, .csv
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      )}

      {error && (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            background: "#FDECEC",
            color: "#B3261E",
            padding: "12px 16px",
            borderRadius: 10,
            marginTop: 16,
            fontSize: 14,
          }}
        >
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* Archivo cargado + mapeo */}
      {rows.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "#EAF7F9",
              padding: "10px 14px",
              borderRadius: 10,
            }}
          >
            <FileSpreadsheet size={18} color={TEAL} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>{fileName}</span>
            <span style={{ color: "#5a6b70", fontSize: 13 }}>
              {rows.length} filas
            </span>
            <button
              onClick={reset}
              style={{
                marginLeft: "auto",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "#5a6b70",
                display: "flex",
              }}
              title="Quitar archivo"
            >
              <X size={18} />
            </button>
          </div>

          {/* Mapeo de columnas */}
          <section
            style={{
              marginTop: 16,
              border: "1px solid #E6EEF0",
              borderRadius: 12,
              padding: 16,
            }}
          >
            <h2 style={{ fontSize: 15, margin: "0 0 12px", fontWeight: 700 }}>
              Columnas
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 12,
              }}
            >
              {[
                ["nombre", "Nombre"],
                ["apellido", "Apellido"],
                ["doc", "Documento *"],
                ["centro", "Centro educativo"],
              ].map(([key, label]) => (
                <label key={key} style={{ fontSize: 13 }}>
                  <span style={{ display: "block", marginBottom: 4, color: "#5a6b70" }}>
                    {label}
                  </span>
                  <select
                    value={map[key]}
                    onChange={(e) =>
                      setMap((m) => ({ ...m, [key]: e.target.value }))
                    }
                    style={selectStyle}
                  >
                    <option value="">—</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>

            {/* Config avanzada */}
            <button
              onClick={() => setShowConfig((v) => !v)}
              style={{
                marginTop: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "transparent",
                border: "none",
                color: TEAL,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
              }}
            >
              <Settings2 size={16} /> Contenido del QR y encabezado
            </button>

            {showConfig && (
              <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
                <label style={{ fontSize: 13 }}>
                  <span style={labelSpan}>
                    Plantilla del QR (variables:{" "}
                    <code>{"{doc} {nombre} {apellido} {fijo1} {fijo2}"}</code>)
                  </span>
                  <input
                    value={tmpl.template}
                    onChange={(e) =>
                      setTmpl((t) => ({ ...t, template: e.target.value }))
                    }
                    style={inputStyle}
                  />
                </label>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <label style={{ fontSize: 13, flex: 1, minWidth: 140 }}>
                    <span style={labelSpan}>Valor fijo 1</span>
                    <input
                      value={tmpl.fijo1}
                      onChange={(e) =>
                        setTmpl((t) => ({ ...t, fijo1: e.target.value }))
                      }
                      style={inputStyle}
                    />
                  </label>
                  <label style={{ fontSize: 13, flex: 1, minWidth: 140 }}>
                    <span style={labelSpan}>Valor fijo 2</span>
                    <input
                      value={tmpl.fijo2}
                      onChange={(e) =>
                        setTmpl((t) => ({ ...t, fijo2: e.target.value }))
                      }
                      style={inputStyle}
                    />
                  </label>
                </div>
                <label style={{ fontSize: 13 }}>
                  <span style={labelSpan}>Título del encabezado</span>
                  <input
                    value={tmpl.title}
                    onChange={(e) =>
                      setTmpl((t) => ({ ...t, title: e.target.value }))
                    }
                    style={inputStyle}
                  />
                </label>
              </div>
            )}
          </section>

          {/* Resumen */}
          {groups && mapReady && (
            <div style={{ marginTop: 16, fontSize: 14, color: "#5a6b70" }}>
              <CheckCircle2
                size={16}
                color={TEAL}
                style={{ verticalAlign: "-3px", marginRight: 6 }}
              />
              {groups.total} personas válidas en{" "}
              <strong>{centrosList.length}</strong> centros.
              {groups.skipped > 0 && (
                <span style={{ color: "#B3261E" }}>
                  {" "}
                  {groups.skipped} filas omitidas (sin documento numérico).
                </span>
              )}
            </div>
          )}

          {!mapReady && (
            <div style={{ marginTop: 12, fontSize: 13, color: "#B3261E" }}>
              Elegí al menos la columna de <strong>documento</strong> y una de
              nombre o apellido.
            </div>
          )}

          {/* Acciones */}
          <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={generateAll}
              disabled={!mapReady || busy}
              style={primaryBtn(!mapReady || busy)}
            >
              {busy && progress ? (
                <>
                  <Loader2 size={16} className="spin" /> Generando{" "}
                  {progress.done}/{progress.total}…
                </>
              ) : (
                <>
                  <Download size={16} /> Generar todos (ZIP)
                </>
              )}
            </button>

            {zipUrl && (
              <a
                href={zipUrl}
                download="QR_por_centro.zip"
                style={{
                  ...primaryBtn(false),
                  background: DARK,
                  textDecoration: "none",
                }}
              >
                <Download size={16} /> Descargar ZIP
              </a>
            )}
          </div>

          {/* Lista de centros con descarga individual */}
          {groups && mapReady && (
            <section style={{ marginTop: 20 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
                Centros ({centrosList.length})
              </h2>
              <div
                style={{
                  border: "1px solid #E6EEF0",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                {centrosList.map(([centro, list], i) => (
                  <div
                    key={centro}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 14px",
                      borderTop: i ? "1px solid #F0F5F6" : "none",
                    }}
                  >
                    <span style={{ fontSize: 14, flex: 1 }}>{centro}</span>
                    <span style={{ fontSize: 13, color: "#7a898d" }}>
                      {list.length}
                    </span>
                    <button
                      onClick={() => downloadOne(centro, list)}
                      disabled={busy}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        border: `1px solid ${TEAL}`,
                        color: TEAL,
                        background: "#fff",
                        borderRadius: 8,
                        padding: "5px 10px",
                        fontSize: 13,
                        cursor: busy ? "not-allowed" : "pointer",
                      }}
                    >
                      <Download size={14} /> PDF
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const selectStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #D5E2E5",
  fontSize: 14,
  background: "#fff",
};
const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #D5E2E5",
  fontSize: 14,
  boxSizing: "border-box",
};
const labelSpan = { display: "block", marginBottom: 4, color: "#5a6b70" };

function primaryBtn(disabled) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: disabled ? "#A9C9CF" : TEAL,
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}